import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { documentOptions } from "../../../../../../assets/data/projectData";
import { RecordStatus } from "../../../../../../assets/data/enumData";

import {
  FormFieldSet,
  Button,
  useFormHandler,
  AttachmentSection,
  showToast,
  Chip,
  Modal,
  DocumentsList,
} from "../../../../../../components";
import { uploadStart } from "../../../../../../redux/slices/universal/fileUploadSlice";
import { addDocumentStart } from "../../../../../../redux/slices/universal/documentsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLoginUser } from "../../../../../../hooks/useLoginUser";
import { addCurrentPlacement } from "../../../../../../redux/slices/placements/placementsSlice";
import { transformFiles } from "../../../../../../utils/transformFiles";
import axiosRequest from "../../../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../../../redux/apiConfig";

const Documents = ({ onCancel, currentPlacement, handleBack, editData }) => {
  const isEditable = !!editData;
  const dispatch = useDispatch();
  const [fetchPerticularData, setFetchPerticularData] = useState(null);
  const { employeeProfileUiid, currentEmployee } = useSelector(
    (state) => state.employee,
  );
  const { userData: loginUserData } = useLoginUser();
  const [attachmentModal, setAttachmentModal] = useState(false);
  const organizationId = loginUserData?.organizationId.uiid;
  const loginUserUiid = loginUserData?.uiid;
  const {
    formData,
    errors,
    handleChange,
    validateAllFields,
    setErrors,
    setFormData,
  } = useFormHandler(
    {
      attachments: [
        {
          id: uuidv4(),
          attachmentName: "",
          attachmentComments: "",
          documentType: "",
          documentStatus: "",
          chooseFiles: [],
        },
      ],
    },
    {
      attachments: [
        {
          // attachmentName: { type: "text", required: true },
          // attachmentComments: { type: "text", required: true },
          documentType: { type: "text", required: true },
          documentStatus: { type: "select", required: true },
          chooseFiles: {
            type: "file",
            required: true,
            // allowedFiles: [
            //   { ext: "png", mime: "image/png" },
            //   { ext: "txt", mime: "text/plain" },
            // ],
          },
        },
      ],
    },
  );

  // This handleAddRow approach directly manipulates state without triggering validation reset
  const handleAddRow = () => {
    // Store current errors
    const currentErrors = { ...errors };
    setFormData({
      ...formData,
      attachments: [
        ...formData.attachments,
        {
          id: uuidv4(),
          attachmentName: "",
          attachmentComments: "",
          documentType: "",
          active: "",
          chooseFiles: [],
        },
      ],
    });
    setErrors(currentErrors);
  };

  const handleRemoveRow = (id) => {
    if (formData.documentRows.length > 1) {
      const indexToRemove = formData.documentRows.findIndex(
        (row) => row.id === id,
      );
      const newDocumentRows = formData.documentRows.filter(
        (row) => row.id !== id,
      );

      setFormData({
        ...formData,
        documentRows: newDocumentRows,
      });
      if (errors?.documentRows) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };

          if (newErrors.documentRows) {
            const updatedRowErrors = [...newErrors.documentRows];
            updatedRowErrors.splice(indexToRemove, 1);
            newErrors.documentRows = updatedRowErrors;
          }

          return newErrors;
        });
      }
    }
  };

  const getAttachmentError = (index, field) => {
    return errors?.attachments?.[index]?.[field] || "";
  };

  const handleFieldChange = (index, field, value) => {
    handleChange(`documentRows[${index}].${field}`, value);
  };
  const handleSave = () => {
    const isValid = validateAllFields();
    if (isValid) {
      // Filter valid document entries
      const cleanAttachments = formData.attachments.filter(
        (att) =>
          att.attachmentName.trim() !== "" ||
          att.documentType.trim() !== "" ||
          (att.chooseFiles && att.chooseFiles.length > 0),
      );

      // Map files with their parent doc id
      const cleanAttachmentsFiles = cleanAttachments.flatMap((doc) =>
        doc.chooseFiles.map((file) => {
          file.id = doc.id;
          return file;
        }),
      );
      const uiid = fetchPerticularData?.uiid ?? "";
      if (!cleanAttachmentsFiles.length) return;
      dispatch(
        uploadStart({
          uploadType: "multiple",
          field: "doc",
          uiid: uiid,
          entity: "placements",
          category: "Documents",
          coll: "Placement",
          doc: cleanAttachmentsFiles,
          onSuccess: (fileData) => {
            const attachmentsPayload = cleanAttachments.map((clean) => {
              return {
                addedBy: loginUserUiid,
                docType: clean.documentType,
                parent: { employee: employeeProfileUiid },
                ref: {
                  id: uiid,
                  entity: "Placement",
                  path: "documents",
                },
                file: (clean.chooseFiles || []).map((file) => {
                  const matchedFile = fileData.find(
                    (f) => f?.fileRefId === clean.id && f.key,
                  );
                  return {
                    name: clean.attachmentName,
                    comments: clean.attachmentComments,
                    status: clean.documentStatus,
                    keys: matchedFile ? [matchedFile.key] : [],
                  };
                }),
              };
            });
            dispatch(
              addDocumentStart({
                coll: "Document",
                refColl: "Placement",
                refId: uiid,
                refField: "documents",
                refPatchId: "",
                payload: {
                  addedBy: loginUserUiid,
                  attachments: attachmentsPayload,
                },
                onSuccess: async () => {
                  dispatch(
                    addCurrentPlacement({
                      documents: attachmentsPayload,
                    }),
                  );
                  handleBack();
                  // handleModalClose(); // Optional: uncomment if you want to close the modal on success
                },
              }),
            );
          },
        }),
      );
    } else {
      showToast("Please fill all the required fields before saving", "error");
    }
  };
  const combineAttachments = Array.isArray(fetchPerticularData?.documents)
    ? transformFiles(fetchPerticularData.documents)
    : [];

  useEffect(() => {
    const fetchData = async () => {
      const uiid =
        currentPlacement?.uiid ??
        editData?.uiid ??
        currentPlacement?.data?.doc?.uiid ??
        "";
      if (!uiid) return;
      const filter = {
        uiid: { type: "NONE", value: uiid },
      };
      try {
        const response = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.PLACEMENTS.GET_BY_UIID,
          params: {
            coll: "Placement",
            ref: "Document",
            fil: JSON.stringify(filter),
            ret: "documents uiid",
            // ref: "InvDoc",
            // incl: "uiid businessName",
          },
        });
        const data = response?.data?.data;
        if (data) {
          //  const invoices = data?.invoices || {};
          setFetchPerticularData(data);
        }
      } catch (error) {
        setFetchPerticularData(null);
        console.error("Failed to fetch placement data:", error);
      }
    };
    fetchData();
  }, [currentPlacement, isEditable]);
  return (
    // <div className="px-4 py-4 border-[1px] border-neutrals-50 rounded-b min-h-[69vh] overflow-auto">
    <div className="px-4 py-4 border-[1px] border-neutrals-50 rounded-b overflow-auto">
      <div>
        <AttachmentSection
          attachments={formData.attachments}
          onChange={(newAttachments) =>
            setFormData({ ...formData, attachments: newAttachments })
          }
          getAttachmentError={getAttachmentError}
          showNameField={true}
          showCommentsField={true}
          showDocumentStatusField={true}
          showDocumentTypeField={true}
        />

        <div className="flex justify-end gap-4 mt-6">
          <Button title={"Cancel"} variant="outlined" onClick={onCancel} />
          <Button title={"Save"} onClick={handleSave} />
        </div>
      </div>
      {combineAttachments?.length > 0 && (
        <div
          className="flex rounded-md items-center cursor-pointer"
          onClick={() => setAttachmentModal(!attachmentModal)}
        >
          <Chip
            label={`Attachments: ${combineAttachments.length}`}
            icon="docs"
            iconPosition="before"
          />
        </div>
      )}

      <Modal
        isOpen={attachmentModal}
        onClose={() => setAttachmentModal(false)}
        // title="Attachment View"
        // width="max-w-5xl"
        width="max-w-2xl"
        title={"Document Details"}
      >
        {/* <div className="flex flex-col justify-center items-center gap-8">
              <h6>attachement Details</h6> */}
        <DocumentsList data={combineAttachments} />
        {/* <Button
                title={"Close"}
                onClick={() => setAttachmentModal(false)}
              /> */}
        {/* </div> */}
      </Modal>
    </div>
  );
};

export default Documents;
